import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useStudents } from '../hooks';
import styles from './Students.module.css';

const Students: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'idle' | 'offline'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'lastActive' | 'progress' | 'score'>('lastActive');
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, refetch } = useStudents({
    search: searchQuery || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    sortBy,
    sortOrder: 'desc',
    page,
    limit: 20,
  });

  const students = data?.data || [];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className={`${styles.status} ${styles.statusActive}`}>Active</span>;
      case 'idle':
        return <span className={`${styles.status} ${styles.statusIdle}`}>Idle</span>;
      default:
        return <span className={`${styles.status} ${styles.statusOffline}`}>Offline</span>;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 85) return styles.scoreHigh;
    if (score >= 70) return styles.scoreMedium;
    return styles.scoreLow;
  };

  const handleRowClick = (studentId: string) => {
    navigate(`/students/${studentId}`);
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner} />
        <span>Loading students...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.error}>
        <span>Failed to load students</span>
        <button type="button" onClick={() => refetch()}>Retry</button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1>Students</h1>
          <p>Manage and monitor student progress</p>
        </div>
        <div className={styles.stats}>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{data?.pagination.total ?? students.length}</span>
            <span className={styles.statLabel}>Total</span>
          </div>
          <div className={styles.statItem}>
            <span className={`${styles.statValue} ${styles.activeValue}`}>
              {students.filter((s) => s.status === 'active').length}
            </span>
            <span className={styles.statLabel}>Active on this page</span>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            type="text"
            aria-label="Search by name or email"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className={styles.searchInput}
          />
          {searchQuery && (
            <button type="button" aria-label="Clear student search" className={styles.clearBtn} onClick={() => {
              setSearchQuery('');
              setPage(1);
            }}>
              ✕
            </button>
          )}
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="student-status">Status:</label>
          <select
            id="student-status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as typeof statusFilter);
              setPage(1);
            }}
            className={styles.select}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="idle">Idle</option>
            <option value="offline">Offline</option>
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="student-sort">Sort by:</label>
          <select
            id="student-sort"
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value as typeof sortBy);
              setPage(1);
            }}
            className={styles.select}
          >
            <option value="lastActive">Last Active</option>
            <option value="name">Name</option>
            <option value="progress">Labs Completed</option>
            <option value="score">Avg Score</option>
          </select>
        </div>
      </div>

      {/* Students Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Student</th>
              <th>Status</th>
              <th>Enrolled</th>
              <th>Completed</th>
              <th>In Progress</th>
              <th>Avg Score</th>
              <th>Last Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {students.map((student) => (
              <tr
                key={student.id}
                onClick={() => handleRowClick(student.id)}
                className={styles.clickableRow}
              >
                <td>
                  <div className={styles.studentInfo}>
                    <div className={styles.avatar}>
                      {student.firstName[0]}
                      {student.lastName[0]}
                    </div>
                    <div>
                      <div className={styles.name}>
                        {student.firstName} {student.lastName}
                      </div>
                      <div className={styles.email}>{student.email}</div>
                    </div>
                  </div>
                </td>
                <td>{getStatusBadge(student.status)}</td>
                <td>
                  <span className={styles.courseCount}>{student.enrolledCourses} courses</span>
                </td>
                <td>
                  <span className={styles.labsCompleted}>{student.labsCompleted}</span>
                </td>
                <td>
                  <span className={styles.labsInProgress}>{student.labsInProgress}</span>
                </td>
                <td>
                  {student.averageScore != null && Number.isFinite(student.averageScore) ? (
                    <span className={`${styles.score} ${getScoreColor(student.averageScore)}`}>
                      {student.averageScore}%
                    </span>
                  ) : (
                    <span className={styles.score}>—</span>
                  )}
                </td>
                <td className={styles.lastActive}>
                  {Number.isFinite(new Date(student.lastActiveAt).getTime()) ? formatDistanceToNow(new Date(student.lastActiveAt), { addSuffix: true }) : '—'}
                </td>
                <td>
                  <div className={styles.actionBtns} onClick={(e) => e.stopPropagation()}>
                    <Link
                      to={`/students/${student.id}`}
                      className={styles.actionBtn}
                      title="View Details"
                      aria-label={`View details for ${student.firstName} ${student.lastName}`}
                    >
                      👁️
                    </Link>
                    {/*
                     * Both actions deep-link into existing pages that
                     * already have full UIs (StudentDetail has a message
                     * thread; Environments page lists per-student envs).
                     * Until inline modals are built, this avoids the dead
                     * button problem.
                     */}
                    <Link
                      to={`/messages?studentId=${encodeURIComponent(student.id)}`}
                      className={styles.actionBtn}
                      title="Send Message"
                      aria-label={`Message ${student.firstName} ${student.lastName}`}
                    >
                      💬
                    </Link>
                    <Link
                      to={`/environments?studentId=${student.id}`}
                      className={styles.actionBtn}
                      title="View Environment"
                      aria-label={`View environments for ${student.firstName} ${student.lastName}`}
                    >
                      🖥️
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {students.length === 0 && (
        <div className={styles.empty}>
          <span className={styles.emptyIcon}>👥</span>
          <p>No students found</p>
          {searchQuery && <span>Try adjusting your search criteria</span>}
        </div>
      )}

      {/* Pagination */}
      {data?.pagination && data.pagination.totalPages > 1 && (
        <div className={styles.pagination}>
          <span className={styles.pageInfo}>
            Showing {students.length} of {data.pagination.total} students
          </span>
          <div className={styles.pageButtons}>
            <button
              disabled={page === 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span>
              Page {page} of {data.pagination.totalPages}
            </span>
            <button
              disabled={page === data.pagination.totalPages}
              onClick={() => setPage(p => Math.min(data.pagination.totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Students;
